import { combineUploads, groupLabel, listUploads, listUserGroups, undoCombine, UPLOAD_LIST_LIMIT } from '../service'
import { libraryEntries } from '../service/gameParts'
import { COMBINE_STARTED, combineConfirmText, combinedPartIds, isCombinable, MAX_COMBINE } from '../service/combine'
import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import SearchIcon from '@mui/icons-material/Search'
import CallMergeRoundedIcon from '@mui/icons-material/CallMergeRounded'
import { useConfirm } from 'material-ui-confirm'
import UploadPreviewCard from '../components/card/UploadPreviewCard'
import CombineDialog from '../components/upload/CombineDialog'
import Messages from '../components/common/Messages'
import { useAuth } from '../auth/AuthContext'
import useRecentSearches, { hasSavedFilterValues } from '../hooks/useRecentSearches'

const emptyFilters = {
  q: '',
  owner: '',
  group: '',
  from: '',
  to: ''
}

const toStartOfDayIso = value => {
  if (!value) return undefined
  return new Date(`${value}T00:00:00.000`).toISOString()
}

const toEndOfDayIso = value => {
  if (!value) return undefined
  return new Date(`${value}T23:59:59.999`).toISOString()
}

const filtersFromSearchParams = searchParams => ({
  q: searchParams.get('q') || '',
  owner: searchParams.get('owner') || '',
  group: searchParams.get('group') || '',
  from: searchParams.get('from') || '',
  to: searchParams.get('to') || ''
})

const paramsFromFilters = (filters, currentParams) => {
  const params = new URLSearchParams()
  if (filters.q) params.set('q', filters.q)
  if (filters.owner) params.set('owner', filters.owner)
  if (filters.group) params.set('group', filters.group)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (currentParams.get('__e2e_authenticated') === '1') {
    params.set('__e2e_authenticated', '1')
  }
  return params
}

const describeUploadSearch = filters => {
  const parts = []
  if (filters.q) parts.push(`Search: ${filters.q}`)
  if (filters.group) parts.push(`Group: ${filters.group}`)
  if (filters.owner) parts.push(`Owner: ${filters.owner}`)
  if (filters.from || filters.to) {
    parts.push(`Date: ${filters.from || 'any'} to ${filters.to || 'any'}`)
  }
  return parts.join(' · ') || 'All uploads'
}

const hasUploadFilterParams = searchParams =>
  ['q', 'owner', 'group', 'from', 'to'].some(key => searchParams.has(key))

function Uploads () {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const {
    lastSearch,
    recentSearches,
    rememberSearch,
    clearLastSearch,
    clearRecentSearches
  } = useRecentSearches({
    userKey: user?.username,
    scope: 'uploads'
  })
  const searchKey = searchParams.toString()
  const applied = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchKey]
  )
  const [draft, setDraft] = useState(applied)
  const [groups, setGroups] = useState([])
  const [uploads, setUploads] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)
  const confirm = useConfirm()
  // "Combine videos" mode: the chosen upload ids, and whether the order dialog is open.
  const [selecting, setSelecting] = useState(false)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [ordering, setOrdering] = useState(false)
  const [combining, setCombining] = useState(false)
  const reloadUploads = useRef(async () => {})

  useEffect(() => {
    if (!lastSearch || hasUploadFilterParams(searchParams)) return
    setSearchParams(paramsFromFilters(lastSearch, searchParams), { replace: true })
  }, [lastSearch, searchKey, searchParams, setSearchParams])

  useEffect(() => {
    setDraft(applied)
  }, [searchKey])

  useEffect(() => {
    let active = true
    listUserGroups()
      .then(rows => {
        if (active) setGroups(rows || [])
      })
      .catch(err => console.error(err))
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true
    const loadUploads = async ({ quiet = false } = {}) => {
      try {
        if (!quiet) setLoading(true)
        setError(null)
        const rows = await listUploads({
          q: applied.q || undefined,
          owner: applied.owner || undefined,
          group: applied.group || undefined,
          fromDate: toStartOfDayIso(applied.from),
          toDate: toEndOfDayIso(applied.to)
        })
        if (active) setUploads(rows)
      } catch (err) {
        console.error(err)
        if (active) setError(err)
      } finally {
        if (active && !quiet) setLoading(false)
      }
    }
    loadUploads()
    reloadUploads.current = () => loadUploads({ quiet: true })
    const timer = window.setInterval(() => loadUploads({ quiet: true }), 15000)
    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [searchKey])

  const applyFilters = () => {
    setSearchParams(paramsFromFilters(draft, searchParams))
    if (hasSavedFilterValues(draft)) {
      rememberSearch(draft, describeUploadSearch(draft))
    } else {
      clearLastSearch()
    }
  }

  const applyRecentSearch = entry => {
    setDraft(entry.filters)
    setSearchParams(paramsFromFilters(entry.filters, searchParams))
    rememberSearch(entry.filters, entry.label)
  }

  const clearFilters = () => {
    setDraft(emptyFilters)
    const params = new URLSearchParams()
    if (searchParams.get('__e2e_authenticated') === '1') {
      params.set('__e2e_authenticated', '1')
    }
    setSearchParams(params)
    clearLastSearch()
  }

  // One card per video; only a game still stored as separate parts shares one card (D34).
  const entries = useMemo(() => libraryEntries(uploads), [uploads])

  const hasFilters = Object.values(applied).some(Boolean)
  // A filtered or cut-off list may hold only some of a game's parts, so its card does not claim the total.
  const partialResults = hasFilters || uploads.length >= UPLOAD_LIST_LIMIT

  // D34 release B: the owner combines separately stored game videos into one. A card stands for one video,
  // or for every part of a game, and can be chosen only when all of them can be combined.
  const combinable = uploads.filter(upload => isCombinable(upload, user?.username))
  const selected = combinable.filter(upload => selectedIds.has(upload.id))
  const entryUploads = entry => entry.parts || [entry.upload]
  const entrySelectable = entry => entryUploads(entry).every(upload => isCombinable(upload, user?.username))
  const selectEntry = (entry, checked) => setSelectedIds(current => {
    const next = new Set(current)
    entryUploads(entry).forEach(upload => checked ? next.add(upload.id) : next.delete(upload.id))
    return next
  })
  const stopSelecting = () => {
    setSelecting(false)
    setOrdering(false)
    setSelectedIds(new Set())
  }

  const runCombine = async (uploadIds, name = null) => {
    setCombining(true)
    try {
      await combineUploads(uploadIds, name)
      setMessage({ severity: 'success', text: COMBINE_STARTED })
      await reloadUploads.current()
      return true
    } catch (err) {
      console.error(err)
      setMessage({ severity: 'error', text: err.detail || 'Unable to combine these videos. Nothing was changed.' })
      return false
    } finally {
      setCombining(false)
    }
  }

  const combineGame = async parts => {
    try {
      await confirm({
        title: 'Combine into one video?',
        description: combineConfirmText(parts.length),
        confirmationText: 'Combine',
        cancellationText: 'Cancel'
      })
    } catch (_) {
      return
    }
    await runCombine(parts.map(part => part.id))
  }

  const combineSelected = async (uploadIds, name) => {
    if (await runCombine(uploadIds, name)) stopSelecting()
  }

  const undo = async upload => {
    try {
      await confirm({
        title: 'Undo this combine?',
        description: 'The combined video and the highlights copied onto it are deleted. The original parts come back unchanged.',
        confirmationText: 'Undo',
        cancellationText: 'Cancel'
      })
    } catch (_) {
      return
    }
    setCombining(true)
    try {
      await undoCombine(upload.id)
      setMessage({ severity: 'success', text: 'Combine undone. The original parts are back.' })
      await reloadUploads.current()
    } catch (err) {
      console.error(err)
      setMessage({ severity: 'error', text: err.detail || 'Unable to undo the combine.' })
    } finally {
      setCombining(false)
    }
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1700, mx: 'auto' }}>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Uploads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse and filter every source video you can access.
          </Typography>
        </Box>
        {combinable.length >= 2 && !selecting
          ? <Button variant="outlined" startIcon={<CallMergeRoundedIcon />} onClick={() => setSelecting(true)} sx={{ flexShrink: 0 }}>
              Combine videos
            </Button>
          : null}
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search uploads"
              placeholder="Game, event, opponent, or filename"
              value={draft.q}
              onChange={event => setDraft(current => ({
                ...current,
                q: event.target.value
              }))}
              onKeyDown={event => {
                if (event.key === 'Enter') applyFilters()
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Owner"
              placeholder="Username"
              value={draft.owner}
              onChange={event => setDraft(current => ({
                ...current,
                owner: event.target.value
              }))}
              onKeyDown={event => {
                if (event.key === 'Enter') applyFilters()
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={groups}
              getOptionLabel={groupLabel}
              value={draft.group || null}
              onChange={(_event, value) => setDraft(current => ({
                ...current,
                group: value || ''
              }))}
              renderInput={params => (
                <TextField {...params} label="Group" placeholder="Any group" />
              )}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              label="From"
              type="date"
              value={draft.from}
              onChange={event => setDraft(current => ({
                ...current,
                from: event.target.value
              }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              label="To"
              type="date"
              value={draft.to}
              onChange={event => setDraft(current => ({
                ...current,
                to: event.target.value
              }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={1}>
            <Stack direction={{ xs: 'row', md: 'column' }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={applyFilters}
                fullWidth
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterAltOffIcon />}
                onClick={clearFilters}
                disabled={!hasFilters && !Object.values(draft).some(Boolean)}
                fullWidth
              >
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {recentSearches.length > 0
          ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Recent searches
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                {recentSearches.map(entry => (
                  <Chip
                    key={entry.signature || entry.savedAt}
                    label={entry.label}
                    size="small"
                    variant="outlined"
                    onClick={() => applyRecentSearch(entry)}
                  />
                ))}
                <Button size="small" color="inherit" onClick={clearRecentSearches}>
                  Clear recent
                </Button>
              </Stack>
            </Box>
            )
          : null}
      </Paper>

      {error
        ? <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography color="error">
              Error while retrieving uploads: {error.message || String(error)}
            </Typography>
          </Paper>
        : null}

      {loading
        ? <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        : null}

      {!loading && !error
        ? <Typography variant="body2" color="text.secondary">
            {`Showing ${entries.length} video${entries.length === 1 ? '' : 's'}.`}
          </Typography>
        : null}

      {!loading && !error && uploads.length === 0
        ? <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6">
              {hasFilters ? 'No uploads match these filters.' : 'No uploads yet.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {hasFilters
                ? 'Clear or adjust the filters to search your accessible library.'
                : 'Add a game or video to start building your library.'}
            </Typography>
          </Paper>
        : null}

      {/* The Box takes the Stack's spacing margin, so the Grid keeps its own negative margins and lines up. */}
      {!loading && uploads.length > 0
        ? <Box>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {entries.map(entry => (
                <Grid item key={entry.upload.id} xs={12} sm={6} lg={4} xl={3} sx={{ display: 'flex' }}>
                  <UploadPreviewCard
                    upload={entry.upload}
                    parts={entry.parts}
                    partial={partialResults}
                    busy={combining}
                    onCombine={combineGame}
                    onRetryCombine={upload => runCombine(combinedPartIds(upload))}
                    onUndoCombine={undo}
                    selectable={selecting && entrySelectable(entry)}
                    selected={entryUploads(entry).every(upload => selectedIds.has(upload.id))}
                    onSelect={checked => selectEntry(entry, checked)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        : null}

      {selecting
        ? <Paper
            elevation={8}
            role="region"
            aria-label="Combine videos"
            sx={{
              position: 'sticky',
              bottom: { xs: 72, md: 16 },
              zIndex: theme => theme.zIndex.appBar - 1,
              px: 2,
              py: 1.25,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
              {selected.length > MAX_COMBINE
                ? `Choose up to ${MAX_COMBINE} videos.`
                : 'Choose your game videos to combine, then set their order.'}
            </Typography>
            <Button color="inherit" onClick={stopSelecting}>Cancel</Button>
            <Button
              variant="contained"
              disabled={combining || selected.length < 2 || selected.length > MAX_COMBINE}
              onClick={() => setOrdering(true)}
            >
              {`Combine ${selected.length} videos`}
            </Button>
          </Paper>
        : null}

      {ordering
        ? <CombineDialog uploads={selected} busy={combining} onCancel={() => setOrdering(false)} onCombine={combineSelected} />
        : null}
      <Messages message={message} onClose={() => setMessage(null)} />
    </Stack>
  )
}

export default Uploads
